import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useBible } from '../../contexts/BibleContext';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../UI/Icon';
import { openExternalUrl } from '../../utils/nativeBridge';
import './Layout.css';

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { isOffline, currentBook, currentChapter } = useBible();
  const { theme } = useTheme();
  const location = useLocation();

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on navigation (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  // Toggle sidebar collapse on desktop
  const toggleSidebarCollapse = () => {
    const newValue = !sidebarCollapsed;
    setSidebarCollapsed(newValue);
    localStorage.setItem('sidebarCollapsed', newValue.toString());
  };

  const navItems = [
    { path: '/reader', icon: 'book', label: 'Read' },
    { path: '/search', icon: 'search', label: 'Search' },
    { path: '/bookmarks', icon: 'bookmark', label: 'Saved' },
    { path: '/journal', icon: 'journal', label: 'Journal' },
    { path: '/plans', icon: 'calendar', label: 'Plans' },
    { path: '/settings', icon: 'settings', label: 'Settings' },
  ];

  return (
    <div className={`layout layout-${theme}`}>
      {/* Mobile Header */}
      {isMobile && (
        <header className="mobile-header">
          <button 
            className="btn btn-ghost btn-icon" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <Icon name={sidebarOpen ? 'close' : 'menu'} />
          </button>
          
          <div className="mobile-header-title">
            {location.pathname === '/reader' && currentBook ? (
              <span>{currentBook.name} {currentChapter?.number || ''}</span>
            ) : (
              <span className="logo-text">TruthBound</span>
            )}
          </div>
          
          <div className="mobile-header-actions">
            {isOffline && (
              <span className="offline-badge" title="Offline Mode">
                <Icon name="offline" size={16} />
              </span>
            )}
          </div>
        </header>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''} ${!isMobile && sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Desktop Logo */}
        {!isMobile && (
          <div className="sidebar-logo">
            <div className="logo-icon">
              <Icon name="bible" size={28} />
            </div>
            {!sidebarCollapsed && (
              <>
                <span className="logo-text">TruthBound</span>
                {isOffline && (
                  <span className="offline-badge" title="Offline Mode">
                    <Icon name="offline" size={14} />
                  </span>
                )}
              </>
            )}
            <button 
              className="sidebar-collapse-btn"
              onClick={toggleSidebarCollapse}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={16} />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `nav-item ${isActive ? 'nav-item-active' : ''}`
              }
              title={sidebarCollapsed ? item.label : undefined}
            >
              <Icon name={item.icon} size={20} />
              {(!sidebarCollapsed || isMobile) && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar Footer */}
        {(!sidebarCollapsed || isMobile) && (
          <div className="sidebar-footer">
            <div className="verse-of-day">
              <p className="verse-text">
                "Your word is a lamp to my feet and a light to my path."
              </p>
              <p className="verse-ref">— Psalm 119:105</p>
            </div>
            
            {/* Ko-fi Support Button */}
            <a 
              href="https://ko-fi.com/H2H11RLINS" 
              target="_blank" 
              rel="noopener noreferrer"
              className="kofi-button"
              onClick={(e) => {
                e.preventDefault();
                openExternalUrl('https://ko-fi.com/H2H11RLINS');
              }}
            >
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{ marginRight: '5px' }}
              >
                <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
              </svg>
              Support
            </a>
          </div>
        )}
        
        {/* Collapsed state support button */}
        {sidebarCollapsed && !isMobile && (
          <div className="sidebar-footer sidebar-footer-collapsed">
            <a 
              href="https://ko-fi.com/H2H11RLINS" 
              target="_blank" 
              rel="noopener noreferrer"
              className="kofi-button kofi-button-collapsed"
              title="Support TruthBound"
              onClick={(e) => {
                e.preventDefault();
                openExternalUrl('https://ko-fi.com/H2H11RLINS');
              }}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
              </svg>
            </a>
          </div>
        )}
      </aside>

      {/* Overlay for mobile sidebar */}
      {isMobile && sidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main className={`main-content ${!isMobile && sidebarCollapsed ? 'main-content-expanded' : ''}`}>
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav className="bottom-nav">
          {[
            { path: '/reader', icon: 'book', label: 'Read' },
            { path: '/search', icon: 'search', label: 'Search' },
            { path: '/bookmarks', icon: 'bookmark', label: 'Saved' },
            { path: '/journal', icon: 'journal', label: 'Journal' },
            { path: '/settings', icon: 'settings', label: 'Settings' },
          ].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `bottom-nav-item ${isActive ? 'active' : ''}`
              }
            >
              <Icon name={item.icon} size={22} />
              <span className="bottom-nav-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

export default Layout;
